import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import DeliverableRow from './DeliverableRow';

// Helper: compute display order string for an item
const getOrderDisplay = (item) => {
  if (item?.customOrder == null) return '—';
  const n = Number(item?.customOrder);
  if (Number.isInteger(n)) return String(n);
  const parts = n?.toFixed(10)?.replace(/0+$/, '')?.split('.');
  const intPart = parts?.[0];
  const decPart = parts?.[1] || '';
  if (decPart?.length >= 2) {
    return `${intPart}.${decPart?.[0]}.${decPart?.slice(1)}`;
  }
  return `${intPart}.${decPart}`;
};

const DeliverableListView = ({ deliverables, resources, phases, onEdit, onDelete, onUpdate, onAddChild, onReorder, onAdjustDates }) => {
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [sortBy, setSortBy] = useState('defined_order');
  const [collapsedItems, setCollapsedItems] = useState(new Set());
  const [dragOverId, setDragOverId] = useState(null);
  const dragItemRef = useRef(null);

  const toggleCollapse = (id) => {
    setCollapsedItems(prev => {
      const next = new Set(prev);
      if (next?.has(id)) next?.delete(id);
      else next?.add(id);
      return next;
    });
  };

  // Sort deliverables based on sortBy
  const sortItems = (items) => {
    if (!items) return [];
    const sorted = [...items];
    if (sortBy === 'defined_order') {
      sorted?.sort((a, b) => {
        const ao = a?.customOrder ?? Infinity;
        const bo = b?.customOrder ?? Infinity;
        if (ao !== bo) return ao - bo;
        return (a?.sortOrder ?? 0) - (b?.sortOrder ?? 0);
      });
    } else if (sortBy === 'start_date') {
      sorted?.sort((a, b) => {
        const ad = a?.startDate ? new Date(a?.startDate) : new Date(9999, 0);
        const bd = b?.startDate ? new Date(b?.startDate) : new Date(9999, 0);
        return ad - bd;
      });
    } else if (sortBy === 'end_date') {
      sorted?.sort((a, b) => {
        const ad = a?.endDate ? new Date(a?.endDate) : new Date(9999, 0);
        const bd = b?.endDate ? new Date(b?.endDate) : new Date(9999, 0);
        return ad - bd;
      });
    }
    return sorted;
  };

  // Build hierarchy: phases → deliverables → tasks → subtasks
  const buildHierarchy = () => {
    const topLevel = sortItems(deliverables?.filter(d =>
      (d?.rowType === 'deliverable' || !d?.rowType) && !d?.parentId
    ));
    const tasks = deliverables?.filter(d => d?.rowType === 'task');
    const subtasks = deliverables?.filter(d => d?.rowType === 'subtask');

    const buildDeliverableTree = (deliverable) => ({
      ...deliverable,
      children: sortItems(tasks?.filter(t => t?.parentId === deliverable?.id))
        ?.map(task => ({
          ...task,
          children: sortItems(subtasks?.filter(st => st?.parentId === task?.id))
        }))
    });

    if (phases?.length > 0) {
      const phaseGroups = phases?.map(phase => ({
        ...phase,
        _isPhase: true,
        children: topLevel
          ?.filter(d => d?.phaseId === phase?.id)
          ?.map(buildDeliverableTree)
      }));
      const unphased = topLevel?.filter(d => !d?.phaseId || !phases?.find(p => p?.id === d?.phaseId));
      if (unphased?.length > 0) {
        phaseGroups?.push({
          id: '__unphased__',
          name: 'Unassigned',
          _isPhase: true,
          children: unphased?.map(buildDeliverableTree)
        });
      }
      return phaseGroups;
    }

    return topLevel?.map(buildDeliverableTree);
  };

  const hierarchy = buildHierarchy();

  // Flatten for filtering
  const flatFiltered = sortItems(deliverables
    ?.filter(d => filterStatus === 'all' || d?.status === filterStatus)
    ?.filter(d => filterType === 'all' || (d?.rowType || 'deliverable') === filterType));

  // Drag handlers
  const handleDragStart = (e, item) => {
    dragItemRef.current = item;
    e?.dataTransfer?.setData('text/plain', item?.id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, item) => {
    e?.preventDefault();
    setDragOverId(item?.id);
  };

  const handleDrop = async (e, targetItem) => {
    e?.preventDefault();
    setDragOverId(null);
    const draggedItem = dragItemRef?.current;
    if (!draggedItem || draggedItem?.id === targetItem?.id) return;

    // Only allow reordering within same type/parent
    const sameType = (draggedItem?.rowType || 'deliverable') === (targetItem?.rowType || 'deliverable');
    const sameParent = draggedItem?.parentId === targetItem?.parentId;
    if (!sameType || !sameParent) return;

    // Get siblings (same type and parent)
    const siblings = sortItems(deliverables?.filter(d => {
      const dt = d?.rowType || 'deliverable';
      const tt = targetItem?.rowType || 'deliverable';
      return dt === tt && d?.parentId === targetItem?.parentId;
    }));

    const dragIdx = siblings?.findIndex(s => s?.id === draggedItem?.id);
    const targetIdx = siblings?.findIndex(s => s?.id === targetItem?.id);
    if (dragIdx === -1 || targetIdx === -1) return;

    // Reorder siblings
    const reordered = [...siblings];
    reordered?.splice(dragIdx, 1);
    reordered?.splice(targetIdx, 0, draggedItem);

    // Assign new custom orders
    const rowType = targetItem?.rowType || 'deliverable';
    const updates = reordered?.map((item, idx) => {
      let newOrder;
      if (rowType === 'deliverable') {
        newOrder = idx + 1;
      } else if (rowType === 'task') {
        // Get parent deliverable order
        const parent = deliverables?.find(d => d?.id === item?.parentId);
        const parentOrder = parent?.customOrder != null ? Math.floor(Number(parent?.customOrder)) : 1;
        newOrder = parseFloat(`${parentOrder}.${String(idx + 1)?.padStart(1, '0')}`);
      } else if (rowType === 'subtask') {
        // Get parent task
        const parentTask = deliverables?.find(d => d?.id === item?.parentId);
        const parentOrder = parentTask?.customOrder != null ? Number(parentTask?.customOrder) : 1.1;
        const parentStr = parentOrder?.toFixed(1);
        newOrder = parseFloat(`${parentStr}${idx + 1}`);
      }
      return { id: item?.id, customOrder: newOrder };
    });

    await onReorder?.(updates);
    dragItemRef.current = null;
  };

  const handleDragEnd = () => {
    setDragOverId(null);
    dragItemRef.current = null;
  };

  const renderRow = (item, depth = 0) => {
    const isCollapsed = collapsedItems?.has(item?.id);
    const hasChildren = item?.children?.length > 0;

    // Phase group header
    if (item?._isPhase) {
      return (
        <div key={item?.id} className="mb-4">
          <div
            className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg cursor-pointer select-none"
            onClick={() => toggleCollapse(item?.id)}
          >
            <Icon name={isCollapsed ? 'ChevronRight' : 'ChevronDown'} size={14} className="text-amber-600" />
            <Icon name="GitBranch" size={14} className="text-amber-600" />
            <span className="text-sm font-bold text-amber-900">{item?.name}</span>
            {item?.status && item?.id !== '__unphased__' && (
              <span className="px-3 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 border border-amber-200">
                {item?.status}
              </span>
            )}
            <span className="text-xs text-amber-700 ml-auto">{item?.children?.length || 0} deliverable{item?.children?.length !== 1 ? 's' : ''}</span>
          </div>
          {!isCollapsed && hasChildren && (
            <div className="mt-2 space-y-2 pl-4 border-l-2 border-amber-200">
              {item?.children?.map(child => renderRow(child, 0))}
            </div>
          )}
          {!isCollapsed && !hasChildren && (
            <div className="mt-2 pl-4 py-3 text-xs text-muted-foreground text-center border-l-2 border-amber-200">
              No deliverables in this phase
            </div>
          )}
        </div>
      );
    }

    const rowType = item?.rowType || 'deliverable';
    const isVisible = filterType === 'all' || rowType === filterType;
    const statusMatch = filterStatus === 'all' || item?.status === filterStatus;

    if (!isVisible || !statusMatch) return null;

    return (
      <div key={item?.id} onDragEnd={handleDragEnd}>
        <div className="relative">
          {/* Collapse toggle for items with children */}
          {hasChildren && (
            <button
              onClick={() => toggleCollapse(item?.id)}
              className={`absolute z-10 flex items-center justify-center w-5 h-5 rounded-full bg-muted border border-border hover:bg-muted/80 transition-colors`}
              style={{ top: '24px', left: depth === 0 ? '-10px' : depth === 1 ? '14px' : '38px' }}
            >
              <Icon name={isCollapsed ? 'ChevronRight' : 'ChevronDown'} size={12} className="text-muted-foreground" />
            </button>
          )}
          <DeliverableRow
            deliverable={item}
            resources={resources}
            allDeliverables={deliverables}
            onEdit={onEdit}
            onDelete={onDelete}
            onUpdate={onUpdate}
            onAddChild={onAddChild}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            isDragOver={dragOverId === item?.id}
          />
        </div>

        {/* Children */}
        {!isCollapsed && hasChildren && (
          <div className="mt-2 space-y-2">
            {item?.children?.map(child => renderRow(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  if (deliverables?.length === 0) {
    return (
      <div className="bg-card rounded-lg border border-border p-12 text-center">
        <Icon name="ListTodo" size={48} className="mx-auto mb-4 text-muted-foreground" />
        <h3 className="text-lg font-semibold text-foreground mb-2">No Items Yet</h3>
        <p className="text-sm text-muted-foreground mb-6">
          Get started by adding your first deliverable to this project.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters & Sort */}
      <div className="bg-card rounded-lg border border-border p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-foreground mb-2">Type</label>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e?.target?.value)}
              className="w-full px-4 py-2 bg-input border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="all">All Types</option>
              <option value="deliverable">Deliverables</option>
              <option value="task">Tasks</option>
              <option value="subtask">Sub-tasks</option>
            </select>
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-foreground mb-2">Status</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e?.target?.value)}
              className="w-full px-4 py-2 bg-input border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="all">All Statuses</option>
              <option value="pending">Not Started</option>
              <option value="in-progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="blocked">Blocked</option>
            </select>
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-foreground mb-2">Sort By</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e?.target?.value)}
              className="w-full px-4 py-2 bg-input border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="defined_order">Defined Order</option>
              <option value="start_date">Start Date</option>
              <option value="end_date">End Date</option>
            </select>
          </div>
          {onAdjustDates && (
            <div className="flex items-end">
              <button
                onClick={onAdjustDates}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-accent hover:text-accent/80 bg-accent/10 hover:bg-accent/20 border border-accent/30 rounded-lg transition-colors whitespace-nowrap"
                title="Adjust all project dates"
              >
                <Icon name="CalendarClock" size={15} />
                Adjust Dates
              </button>
            </div>
          )}
        </div>
      </div>
      {/* Summary counts */}
      <div className="flex items-center gap-4 text-sm text-muted-foreground">
        {phases?.length > 0 && (
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-amber-500"></span>
            {phases?.length} Phase{phases?.length !== 1 ? 's' : ''}
          </span>
        )}
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-purple-500"></span>
          {deliverables?.filter(d => !d?.rowType || d?.rowType === 'deliverable')?.length} Deliverables
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-blue-500"></span>
          {deliverables?.filter(d => d?.rowType === 'task')?.length} Tasks
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-green-500"></span>
          {deliverables?.filter(d => d?.rowType === 'subtask')?.length} Sub-tasks
        </span>
        {sortBy !== 'defined_order' && (
          <span className="flex items-center gap-1.5 ml-auto text-xs text-primary">
            <Icon name="ArrowUpDown" size={12} />
            Sorted by {sortBy === 'start_date' ? 'Start Date' : 'End Date'}
          </span>
        )}
      </div>
      {/* Hierarchical List */}
      <div className="space-y-3 relative pl-3">
        {filterType === 'all'
          ? hierarchy?.map(item => renderRow(item, 0))
          : flatFiltered?.map(item => (
              <DeliverableRow
                key={item?.id}
                deliverable={item}
                resources={resources}
                allDeliverables={deliverables}
                onEdit={onEdit}
                onDelete={onDelete}
                onUpdate={onUpdate}
                onAddChild={onAddChild}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                isDragOver={dragOverId === item?.id}
              />
            ))
        }
      </div>
      {hierarchy?.length === 0 && filterType !== 'all' && (
        <div className="bg-card rounded-lg border border-border p-8 text-center">
          <Icon name="Filter" size={32} className="mx-auto mb-3 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">No items match your current filters.</p>
        </div>
      )}
    </div>
  );
};

export default DeliverableListView;